import { useState } from "react";
import type { FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daihf5bp14a1h3mr74a0";

const typeOptions = ["明星动态", "影视热剧", "综艺现场", "网红圈", "时尚造型", "音乐现场"];

export default function TipForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const trap = String(formData.get("contact_alt") ?? "").trim();
    if (trap) {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("contact_alt");

    setStatus("loading");
    setErrorMsg("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed:
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setErrorMsg(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，请稍后重试。");
    }
  };

  return (
    <form
      id="tip-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="font-label text-xs font-medium text-foreground-700">投稿标题</span>
          <input
            type="text"
            name="title"
            required
            placeholder="用一句话概括你想聊的话题"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs font-medium text-foreground-700">话题分类</span>
          <select
            name="category"
            required
            defaultValue=""
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
          >
            <option value="" disabled>
              请选择分类
            </option>
            {typeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs font-medium text-foreground-700">联系邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于核实与回复"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </label>

        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="font-label text-xs font-medium text-foreground-700">话题线索说明</span>
          <textarea
            name="detail"
            required
            maxLength={500}
            rows={4}
            placeholder="简单说清时间、场合与已知的公开信息，最多 500 字"
            className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          ></textarea>
        </label>

        <input
          type="text"
          name="contact_alt"
          className="field-trap"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "loading"}
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-send-plane-fill h-4 w-4 flex items-center justify-center"></i>
          {status === "loading" ? "提交中…" : "提交线索"}
        </button>
        <p className="font-label text-xs text-foreground-500">
          提交后我们会进行核实与脱敏处理，感谢你提供线索。
        </p>
      </div>

      {status === "success" ? (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-fill mt-0.5 h-4 w-4 flex items-center justify-center text-secondary-600"></i>
          提交成功，我们已收到你的线索，核实后会在榜单中更新。
        </div>
      ) : null}

      {status === "error" ? (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
          <i className="ri-error-warning-fill mt-0.5 h-4 w-4 flex items-center justify-center text-primary-600"></i>
          {errorMsg}
        </div>
      ) : null}
    </form>
  );
}